import { useCallback, useEffect, useRef, useState } from 'react';
import { Subscription } from 'rxjs';
import * as ApiModels from '@/typings/questionnaires-api-models';
import { getNoodleDb } from '../rxdb.noodle';

type Config = {
  realtime?: boolean;
  enabled?: boolean;
};

type FieldTemplatesWLinkedList = ApiModels.FieldTemplateItem & {
  linkedListVirtualFieldTemplates?: ApiModels.FieldTemplateItem[];
};

function useQuestionnaireFieldTemplates(
  questionnaireTemplateId?: string,
  { realtime = true, enabled = true }: Config = {},
): ApiModels.FieldTemplateItem[] | null {
  const subscription = useRef<Subscription | null>(null);
  const [fieldTemplates, setFieldTemplates] = useState<ApiModels.FieldTemplateItem[] | null>(null);

  const getFieldTemplates = useCallback(async () => {
    try {
      if (!questionnaireTemplateId) return;

      const db = await getNoodleDb();

      subscription.current = db.questionnaireFieldTemplate
        .find({ selector: { questionnaireTemplateId }, sort: [{ rank: 'asc' }] })
        .$.subscribe(rxQuestionnaireFieldTemplates => {
          const linkedListVirtual: ApiModels.FieldTemplateItem[] = [];
          const questionnaireFieldTemplates = rxQuestionnaireFieldTemplates.map(rxQuestionnaireFieldTemplate => {
            const { linkedListVirtualFieldTemplates, ...fieldTemplate } = rxQuestionnaireFieldTemplate.toMutableJSON() as FieldTemplatesWLinkedList;

            if (linkedListVirtualFieldTemplates) {
              linkedListVirtual.push(...linkedListVirtualFieldTemplates);
            }

            return fieldTemplate;
          }) as ApiModels.FieldTemplateItem[];

          const questionnaireFieldTemplatesWLinkedList = [...questionnaireFieldTemplates, ...linkedListVirtual].sort((a, b) => a.rank - b.rank);

          setFieldTemplates(questionnaireFieldTemplatesWLinkedList);

          // If not realtime, unsubscribe after first value is set
          if (!realtime) {
            subscription.current?.unsubscribe();
          }
        });
    } catch (error) {
      console.error('RXDB useQuestionnaireFieldTemplates error - getFieldTemplates()', error);
    }
  }, [questionnaireTemplateId, realtime]);

  useEffect(() => {
    if (enabled) {
      getFieldTemplates();
    }

    return () => {
      subscription.current?.unsubscribe();
    };
  }, [getFieldTemplates, enabled]);

  return fieldTemplates;
}

export default useQuestionnaireFieldTemplates;
